import axios from 'axios';
import to from 'await-to-js';
import { isEmpty, get, groupBy, map, reverse, round, find, some, set } from 'lodash';
import moment from 'moment';
import downloadXlsxFile from '../utils/download-xlsx-file';
import { formatFilters } from '../utils/filters';
import { dayMonthYearFormat } from '@enums/date-formats';
import { storegroupOption } from '@enums/filter-options';

const getInitialState = () => {
  return {
    architectureProducts: [],
    architectureProductsLoading: false,
    architectureProductsCount: 0,
    architectureProductsLoaded: false,
    // Line pricing products should be separated from architectureProducts, because different filter can be applied
    linePricingProducts: [],
    linePricingProductsLoading: false,
    filteredAttributes: [],
    linePricingGroups: [],
    busyImportingLinePricingGroups: false,
    counts: {},
    uploadError: '',
    loading: false, // is not shared for all actions! there is architectureProductsLoading too
    activeLinePricingGroupIndex: null,
    selectedProducts: [],
    finalImpactUpdateAvailable: false,
    // note newTab state needs to be in localStorage to work. see webtool/client/js/store/index.js VuexPersist
    newTabOpenLinePricingRules: false,
    newTabOpenArchitectureRules: false,
    newTabActiveLinePricingGroupIndex: null,
    newTabOpenArchitectureSubGroupSplittingAttributes: null,
    downloadingLinePricing: false,
    selectedArchitectureRulesTSGs: [],
    // Persisted column state for Line Pricing Editor (per storage key)
    linePricingEditorColumnState: {},
  };
};

const store = {
  namespaced: true,

  state: getInitialState(),

  getters: {
    linePricingArchitectureProducts: state =>
      state.linePricingProducts.map(product => ({
        _id: get(product, '_id'),
        productKey: get(product, 'article.productKey'),
        productKeyDisplay: get(product, 'article.productKeyDisplay'),
        toolStoreGroupKey: get(product, 'toolStoreGroupKey'),
        toolStoreGroupDescription: get(product, 'toolStoreGroupDescription'),
        description: get(product, 'article.productDescription'),
        size: get(product, 'article.productSizeType'),
        livePrice: get(product, 'livePrice'),
        intentionCost: get(product, 'intentionCost'),
        packageTypeDescription: get(product, 'attributes.packageTypeDescription'),
        distribution: get(product, 'attributes.distribution'),
        normWeight: get(product, 'attributes.normWeight'),
        normWeightUnitOfMeasure: get(product, 'attributes.normWeightUnitOfMeasure'),
        distributionByAssortmentRole: get(product, 'attributes.distributionByAssortmentRole'),
        subGroups: get(product, 'subGroups'),
      })),
    hasMultipleSubGroups: () => products => {
      const subGroups = new Set();
      return some(products, product => {
        const subGroupValues = get(product, 'subGroups', []);
        const subGroupKey = subGroupValues.length ? subGroupValues.join('-') : '-';
        subGroups.add(subGroupKey);
        return subGroups.size > 1;
      });
    },
    // Return persisted raw column state (transformation moved to component helpers)
    getLinePricingEditorColumnState: state => storageKey => {
      return (state.linePricingEditorColumnState || {})[storageKey] || null;
    },
  },

  mutations: {
    setProducts(state, { architectureProducts }) {
      state.architectureProducts = architectureProducts;
    },

    setProductsCount(state, { count }) {
      state.architectureProductsCount = count;
    },

    setLoading(state, loading) {
      state.loading = loading;
    },

    setArchitectureProductsLoading(state, loading) {
      state.architectureProductsLoading = loading;
    },

    setArchitectureProductsLoaded(state, loadingState) {
      state.architectureProductsLoaded = loadingState;
    },

    setFilteredAttributes(state, { filteredAttributes }) {
      state.filteredAttributes = filteredAttributes;
    },

    setLinePricingGroups(state, { linePricingGroups }) {
      state.linePricingGroups = linePricingGroups;
    },

    setActiveLinePricingGroupIndex(state, { activeLinePricingGroupIndex }) {
      state.activeLinePricingGroupIndex = activeLinePricingGroupIndex;
    },

    setSelectedProducts(state, products) {
      state.selectedProducts = products;
    },

    addSelectedProduct(state, product) {
      state.selectedProducts.push(product);
    },

    removeSelectedProduct(state, product) {
      state.selectedProducts.splice(state.selectedProducts.indexOf(product), 1);
    },

    setFinalImpactUpdateAvailable(state, isAvailable) {
      state.finalImpactUpdateAvailable = isAvailable;
    },

    setNewTabOpenLinePricingRules(state, newTabOpenLinePricingRules) {
      state.newTabOpenLinePricingRules = newTabOpenLinePricingRules;
    },

    setNewTabOpenArchitectureRules(state, newTabOpenArchitectureRules) {
      state.newTabOpenArchitectureRules = newTabOpenArchitectureRules;
    },

    setNewTabActiveLinePricingGroupIndex(state, newTabActiveLinePricingGroupIndex) {
      state.newTabActiveLinePricingGroupIndex = newTabActiveLinePricingGroupIndex;
    },

    setNewTabOpenArchitectureSubGroupSplittingAttributes(state, architectureSubGroupDescription) {
      state.newTabOpenArchitectureSubGroupSplittingAttributes = architectureSubGroupDescription;
    },

    setDownloadingLinePricing(state, downloading) {
      state.downloadingLinePricing = downloading;
    },

    setSelectedArchitectureRulesTSGs(state, storeGroups) {
      state.selectedArchitectureRulesTSGs = storeGroups;
    },

    setLinePricingProducts(state, { linePricingProducts }) {
      state.linePricingProducts = linePricingProducts;
    },

    setLinePricingProductsLoading(state, loading) {
      state.linePricingProductsLoading = loading;
    },

    setBusyImportingLinePricingGroups(state, importing) {
      state.busyImportingLinePricingGroups = importing;
    },

    setAffectedCounts(state, counts) {
      state.counts = counts;
    },

    setErrorState(state, error) {
      state.uploadError = error;
    },

    resetState(state) {
      Object.assign(state, getInitialState());
    },

    // Save/overwrite column state for Line Pricing Editor under a storageKey
    setLinePricingEditorColumnState(state, { storageKey, columnState }) {
      if (!storageKey) return;
      state.linePricingEditorColumnState = {
        ...state.linePricingEditorColumnState,
        [storageKey]: columnState,
      };
    },
  },

  actions: {
    setNewTabOpenLinePricingRules({ commit }, newTabOpenLinePricingRules) {
      commit('setNewTabOpenLinePricingRules', newTabOpenLinePricingRules);
    },

    setNewTabOpenArchitectureRules({ commit }, newTabOpenArchitectureRules) {
      commit('setNewTabOpenArchitectureRules', newTabOpenArchitectureRules);
    },

    setNewTabActiveLinePricingGroupIndex({ commit }, newTabActiveLinePricingGroupIndex) {
      commit('setNewTabActiveLinePricingGroupIndex', newTabActiveLinePricingGroupIndex);
    },

    setNewTabOpenArchitectureSubGroupSplittingAttributes(
      { commit },
      architectureSubGroupDescription
    ) {
      commit(
        'setNewTabOpenArchitectureSubGroupSplittingAttributes',
        architectureSubGroupDescription
      );
    },

    // Allow components to dispatch an action to persist column state (keeps API consistent)
    setLinePricingEditorColumnState({ commit }, payload) {
      commit('setLinePricingEditorColumnState', payload);
    },

    resetNewTabState({ commit }) {
      commit('setNewTabOpenLinePricingRules', null);
      commit('setNewTabOpenArchitectureRules', null);
      commit('setNewTabActiveLinePricingGroupIndex', null);
      commit('setNewTabOpenArchitectureSubGroupSplittingAttributes', null);
    },

    async consumeNewTabOpenLinePricingRules({ state, dispatch }) {
      const newTabOpenLinePricingRules = state.newTabOpenLinePricingRules;
      await dispatch('setNewTabOpenLinePricingRules', null);
      return newTabOpenLinePricingRules;
    },

    async consumeNewTabOpenArchitectureRules({ state, dispatch }) {
      const newTabOpenArchitectureRules = state.newTabOpenArchitectureRules;
      await dispatch('setNewTabOpenArchitectureRules', null);
      return newTabOpenArchitectureRules;
    },

    async consumeNewTabActiveLinePricingGroupIndex({ state, dispatch }) {
      const newTabActiveLinePricingGroupIndex = state.newTabActiveLinePricingGroupIndex;
      await dispatch('setNewTabActiveLinePricingGroupIndex', null);
      return newTabActiveLinePricingGroupIndex;
    },

    async consumeNewTabOpenArchitectureSubGroupSplittingAttributes({ state, dispatch }) {
      const newTabOpenArchitectureSubGroupSplittingAttributes =
        state.newTabOpenArchitectureSubGroupSplittingAttributes;
      await dispatch('setNewTabOpenArchitectureSubGroupSplittingAttributes', null);
      return newTabOpenArchitectureSubGroupSplittingAttributes;
    },

    async updateFinalImpacts({ commit, rootState, state }) {
      const architectureProducts = state.architectureProducts;
      const [err, response] = await to(
        axios.patch(
          `/api/architecture-drivers/workpackage/${
            rootState.workpackages.selectedWorkpackage._id
          }/update-product-final-impacts/${rootState.filters.architectureGroup}/${
            rootState.filters.scenario
          }`,
          { architectureProducts }
        )
      );
      if (err) {
        throw new Error(err.message);
      }

      commit('setFinalImpactUpdateAvailable', false);
      return response.data;
    },

    calculateFinalImpact(
      { commit, state },
      { productKey, toolStoreGroupKey, impactFactor, offset }
    ) {
      const architectureProducts = state.architectureProducts;
      const finalImpact = impactFactor * offset;

      const architectureProduct = find(
        architectureProducts,
        product =>
          product.article.productKey === productKey &&
          product.toolStoreGroupKey === toolStoreGroupKey
      );
      architectureProduct.productImpact.impactFactorFinal = round(finalImpact, 3);
      architectureProduct.productImpact.impactFactorCustomOffset = round(offset, 2);

      commit('setProducts', { architectureProducts });
      commit('setFinalImpactUpdateAvailable', true);
    },

    async fetchArchitectureProducts({ commit, rootState, state }, params = {}) {
      const scenarioKey = rootState.filters.scenario;
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;

      const architectureGroupId = get(
        params,
        'architectureGroupId',
        get(rootState, 'filters.architectureGroup', null)
      );

      if (!architectureGroupId || !scenarioKey) return;

      commit('setFinalImpactUpdateAvailable', true);
      commit('setArchitectureProductsLoading', true);

      const requestParams = {
        architectureGroupId,
      };

      if (!isEmpty(state.selectedArchitectureRulesTSGs)) {
        const whereClause = {
          attributeFilterType: storegroupOption,
          attributeValue: state.selectedArchitectureRulesTSGs,
        };

        const formattedWhere = formatFilters({
          where: [whereClause],
          rootState,
        });

        requestParams.where = JSON.stringify(formattedWhere);
      }

      const [err, { data: architectureProducts }] = await to(
        axios.get(
          `/api/workpackage-product/workpackage/${workpackageId}/architecture-group/${architectureGroupId}/${scenarioKey}`,
          { params: requestParams }
        )
      );

      if (err) {
        commit('setArchitectureProductsLoading', false);
        throw new Error(err.message);
      }

      commit('setProducts', { architectureProducts });
      commit('setProductsCount', { count: architectureProducts.length });
      commit('setFinalImpactUpdateAvailable', false);
      commit('setArchitectureProductsLoaded', true);
      commit('setArchitectureProductsLoading', false);
    },

    async getFilteredAttributes(
      { commit, rootState },
      { params = {}, additionalFilters = {} } = {}
    ) {
      commit('setLoading', true);

      const { where } = params;

      if (isEmpty(where)) {
        commit('setFilteredAttributes', { filteredAttributes: [] });
        commit('setLoading', false);
        return;
      }

      params.where = formatFilters({ where, rootState });
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;

      if (additionalFilters.architectureGroupId) {
        set(params, 'additionalFilters.architectureGroupId', additionalFilters.architectureGroupId);
      }

      const [err, { data: filteredAttributes }] = await to(
        axios.get(`/api/attributes/workpackage/${workpackageId}`, { params })
      );
      if (err) {
        commit('setLoading', false);
        throw new Error(err.message);
      }

      commit('setFilteredAttributes', { filteredAttributes });
      commit('setLoading', false);
    },

    async getLinePricingGroupForProduct({ rootState }, { productKey, toolStoreGroupKey }) {
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      const architectureGroupId = rootState.filters.architectureGroup;
      const scenarioKey = rootState.filters.scenario;
      const params = {
        architectureGroupId,
        toolStoreGroupKey,
      };
      const [err, res] = await to(
        axios.get(
          `/api/line-priced-products/workpackage/${workpackageId}/scenario/${scenarioKey}/product/${productKey}`,
          {
            params,
          }
        )
      );
      if (err) {
        throw new Error(err.message);
      }

      return res.data;
    },

    async fetchLinePricingGroupsWithProducts({ commit, rootState, dispatch }) {
      const architectureGroupId = rootState.filters.architectureGroup;
      const scenarioKey = rootState.filters.scenario;
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;

      // line pricing groups and their products should be fetched only when both architecture group and scenario selected
      if (!architectureGroupId || !scenarioKey) return;

      commit('setLoading', true);

      const params = {
        where: {
          architectureGroupId,
          scenarioKey,
        },
      };

      const [err, response] = await to(
        axios.get(`/api/line-pricing-groups/workpackage/${workpackageId}`, { params })
      );
      if (err) {
        commit('setLoading', false);
        throw new Error(err.message);
      }

      const { data: linePricingGroups } = response;
      const products = await dispatch('fetchLinePricedProducts', {
        linePricingGroups,
        scenarioKey,
      });

      const linePricedProductsByGroup = groupBy(products, 'linePricingGroupKey');
      const linePricingGroupsWithProducts = map(linePricingGroups, linePricingGroup => {
        const linePricedProducts = linePricedProductsByGroup[linePricingGroup.linePricingGroupKey];
        // If there are no product an empty array must return
        return {
          ...linePricingGroup,
          products: !linePricedProducts ? [] : linePricedProducts,
        };
      });

      commit('setLinePricingGroups', {
        linePricingGroups: reverse(linePricingGroupsWithProducts),
      });
      commit('setLoading', false);
    },

    async fetchLinePricedProducts(
      { commit, rootState },
      { linePricingGroups = [], scenarioKey } = {}
    ) {
      const architectureGroupId = rootState.filters.architectureGroup;
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;

      if (!architectureGroupId || !linePricingGroups.length || !scenarioKey) {
        return Promise.resolve([]);
      }

      // get all products for the selected groups
      const linePricedProductsParams = {
        linePricingGroupKeys: JSON.stringify(linePricingGroups.map(i => i.linePricingGroupKey)),
        architectureGroupId,
      };

      // Use the endpoint that returns products with complete details
      const [err, { data: linePricedProducts }] = await to(
        axios.get(
          `/api/line-priced-products/workpackage/${workpackageId}/${scenarioKey}/with-details`,
          { params: linePricedProductsParams }
        )
      );

      if (err) {
        commit('setLoading', false);
        throw new Error(err.message);
      }

      return linePricedProducts;
    },

    setActiveLinePricingGroupIndex({ commit }, { activeLinePricingGroupIndex }) {
      commit('setActiveLinePricingGroupIndex', { activeLinePricingGroupIndex });
    },

    async saveLinePricingGroupsWithProducts(
      { commit, dispatch, rootState },
      { linePricingGroups = {} } = {}
    ) {
      commit('setLoading', true);
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      const [err] = await to(
        axios.post(
          `/api/line-pricing-groups/workpackage/${workpackageId}/with-products`,
          linePricingGroups
        )
      );
      if (err) {
        commit('setLoading', false);
        throw new Error(err.message);
      }
      commit('setLoading', false);
      dispatch('fetchLinePricingGroupsWithProducts');
    },

    async deleteLinePricingGroup({ commit, dispatch, rootState }, linePricingGroupKey) {
      if (!linePricingGroupKey) return;
      commit('setLoading', true);

      // deletes by (linePricingGroupKey, sceanrioKey, workpackageId)
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      const scenarioKey = rootState.filters.scenario;
      const params = {
        where: {
          scenarioKey,
          linePricingGroupKey,
        },
      };

      const [[linePricingError], [productsError]] = await Promise.all([
        to(axios.delete(`/api/line-pricing-groups/workpackage/${workpackageId}`, { params })),
        to(axios.delete(`/api/line-priced-products/workpackage/${workpackageId}`, { params })),
      ]);

      const err = linePricingError || productsError;

      if (err) {
        commit('setLoading', false);
        throw new Error(err.message);
      }

      commit('setLoading', false);
      dispatch('fetchLinePricingGroupsWithProducts');
    },

    async createLinePricingGroup({ rootState, dispatch, commit }, payload) {
      commit('setLoading', true);
      try {
        const workpackageId = rootState.workpackages.selectedWorkpackage._id;
        const body = {
          linePricedGroupDescription: payload.linePricedGroupDescription,
          architectureGroupId: payload.architectureGroupId,
          scenarioKey: payload.scenarioKey,
        };
        const [err] = await to(
          axios.post(`/api/line-pricing-groups/workpackage/${workpackageId}`, body)
        );
        if (err) {
          if (err.response && err.response.status === 409) {
            throw Object.assign(new Error('duplicate'), { code: 409 });
          }
          throw err;
        }
        await dispatch('fetchLinePricingGroupsWithProducts');
      } finally {
        commit('setLoading', false);
      }
    },

    async editLinePricingGroup(
      { rootState, dispatch, commit },
      { linePricingGroupKey, linePricedGroupDescription }
    ) {
      commit('setLoading', true);
      try {
        const workpackageId = rootState.workpackages.selectedWorkpackage._id;
        const scenarioKey = rootState.filters.scenario;
        const [err] = await to(
          axios.patch(
            `/api/line-pricing-groups/workpackage/${workpackageId}/linePricingGroupKey/${linePricingGroupKey}`,
            { scenarioKey, linePricedGroupDescription }
          )
        );
        if (err) {
          if (err.response && err.response.status === 409) {
            throw Object.assign(new Error('duplicate'), { code: 409 });
          }
          throw err;
        }
        await dispatch('fetchLinePricingGroupsWithProducts');
      } finally {
        commit('setLoading', false);
      }
    },

    async deleteLinePricedProduct({ commit }, { linePricedProduct = {} } = {}) {
      commit('setLoading', true);

      const { _id } = linePricedProduct;

      if (!_id) {
        commit('setLoading', false);
        return;
      }

      const [err] = await to(axios.delete(`/api/line-priced-products/${_id}`));
      if (err) {
        commit('setLoading', false);
        throw new Error(err.message);
      }

      commit('setLoading', false);
    },

    addSelectedProduct({ commit }, { product }) {
      commit('addSelectedProduct', product);
    },

    setSelectedProducts({ commit }, { products }) {
      commit('setSelectedProducts', products);
    },

    removeSelectedProduct({ commit }, { product }) {
      commit('removeSelectedProduct', product);
    },

    clearSelectedProducts({ commit }) {
      commit('setSelectedProducts', []);
    },

    resetState({ commit }) {
      commit('resetState');
    },

    addExportLinePricingNotification({ commit }, { selectedWorkpackage, jobStatus }) {
      const jobId = 'export_line_pricing';
      const baseTranslation = `notifications.${jobId}`;
      const notification = {
        id: `${jobId}:${jobStatus}:${Date.now()}`,
        jobStatus,
        notificationPayload: selectedWorkpackage.description,
        baseTranslation,
      };
      commit('notifications/addNotification', notification, { root: true });
    },

    async downloadLinePricing(
      { rootState, commit, dispatch },
      { translationMap, architectureGroupId, columnFormatters = {} }
    ) {
      commit('setDownloadingLinePricing', true);
      const selectedWorkpackage = rootState.workpackages.selectedWorkpackage;
      const baseURL = '/api/line-pricing-export';
      const genericURL = `/workpackage/${selectedWorkpackage._id}/line-pricing-results`;
      const url = architectureGroupId
        ? `${baseURL}/ag${genericURL}`
        : `${baseURL}/all${genericURL}`;
      const params = architectureGroupId
        ? { translationMap, architectureGroupId }
        : { translationMap };
      const [err, res] = await to(axios.get(url, { params }));
      if (err) {
        dispatch('addExportLinePricingNotification', {
          selectedWorkpackage,
          jobStatus: 'failed',
        });
        commit('setDownloadingLinePricing', false);
        throw new Error(err.message);
      }
      const linePricingData = res && res.data;
      if (isEmpty(linePricingData)) {
        dispatch('addExportLinePricingNotification', {
          selectedWorkpackage,
          jobStatus: 'no_results',
        });
        commit('setDownloadingLinePricing', false);
        return;
      }
      const timestamp = moment().format(dayMonthYearFormat);
      downloadXlsxFile(linePricingData, `linepricing_extract_${timestamp}.xlsx`, {
        columnFormatters,
      });
      dispatch('addExportLinePricingNotification', {
        selectedWorkpackage,
        jobStatus: 'finished',
      });
      commit('setDownloadingLinePricing', false);
    },

    updateSelectedArchitectureRulesTSGs({ commit, dispatch, rootState }, storeGroups) {
      commit('setSelectedArchitectureRulesTSGs', storeGroups);
      // Optionally refresh architecture products based on the new selection
      const architectureGroupId = rootState.filters.architectureGroup;
      if (architectureGroupId) {
        dispatch('fetchArchitectureProducts', { architectureGroupId });
      }
    },

    initializeArchitectureRulesTSGs({ commit, rootGetters }) {
      const mainToolStoreGroupKeys = rootGetters['storeGroupRelationships/mainToolStoreGroupKeys'];
      commit('setSelectedArchitectureRulesTSGs', [...mainToolStoreGroupKeys]);
    },

    async fetchLinePricingProducts({ commit, rootState }, params = {}) {
      const scenarioKey = rootState.filters.scenario;
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;

      // Use payload architectureGroupId if provided, otherwise fallback to rootState
      const architectureGroupId = get(
        params,
        'architectureGroupId',
        get(rootState, 'filters.architectureGroup', null)
      );

      // Architecture products should be fetched only when both architecture group and scenario selected
      if (!architectureGroupId || !scenarioKey) return;

      commit('setLinePricingProductsLoading', true);

      const requestParams = {
        architectureGroupId,
      };

      if (!isEmpty(get(params, 'where'))) {
        const formattedWhere = formatFilters({
          where: params.where,
          rootState,
        });
        requestParams.where = JSON.stringify(formattedWhere);
      }

      const [err, { data: linePricingProducts }] = await to(
        axios.get(
          `/api/workpackage-product/workpackage/${workpackageId}/architecture-group/${architectureGroupId}/${scenarioKey}`,
          { params: requestParams }
        )
      );

      if (err) {
        commit('setLinePricingProductsLoading', false);
        throw new Error(err.message);
      }

      commit('setLinePricingProducts', { linePricingProducts });
      commit('setLinePricingProductsLoading', false);
    },
    async uploadLPGDataFile({ rootState, commit }, dataFile) {
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      const [err, res] = await to(
        axios.post(
          `/api/line-pricing-groups/workpackage/${workpackageId}/import-lpgs/validate`,
          dataFile,
          {
            headers: {
              // Now axios 1.x set the Content-Type to 'application/json' automatically so in order to have the value as 'FormData/HTMLForm' we need to set as undefined
              // You can read more about in the axios migration guide to v1.x on https://github.com/bmuenzenmeyer/axios-1.0.0-migration-guide?tab=readme-ov-file#multipart-form-data-is-no-longer-automatically-set
              'Content-Type': undefined,
            },
          }
        )
      );
      if (err) {
        commit('setErrorState', err.response.data);
        throw err.response.data;
      }
      if (res.data.error) {
        commit('setErrorState', res.data.error);
      } else {
        commit('setAffectedCounts', res.data.counts);
      }
      return res;
    },

    async confirmLGPUpload({ rootState, commit }, { primaryColumn }) {
      try {
        commit('setBusyImportingLinePricingGroups', true);
        const workpackageId = rootState.workpackages.selectedWorkpackage._id;
        const res = await axios.patch(
          `/api/line-pricing-groups/workpackage/${workpackageId}/import-lpgs/confirm`,
          {
            params: { primaryColumn },
          }
        );
        return res;
      } finally {
        commit('setBusyImportingLinePricingGroups', false);
      }
    },
  },
};

export default store;
